'use client';

/** Shared base for empty/error states, providing common styling and structure for EmptyState and ErrorState. Not for direct use. */

import { SECTION_TITLE_TAG_DEFAULT } from '@federa/const/ui-heading-levels';
import { colors } from '../css-refs.colors.ts';
import { typography } from '../css-refs.typography.ts';
import { spacing } from '../css-refs.spacing.ts';
import { borderRadius } from '../css-refs.effects.ts';
import { ACTION_BUTTON_PADDING_STYLE, MUTED_LABEL_SECONDARY, LAYOUT_CLASSES } from '../common/layout-styles.ts';
import { BORDER_DEFAULT, cursor } from '../common/style-primitives.ts';
import { Button } from '../primitives/Button.tsx';
import type { ButtonVariant } from '@federa/types/ui-button';
import type { ComponentSize } from '@federa/types/ui-design-tokens';
import type { StateMessageAction, StateMessageAriaLive, StateMessageRole } from '@federa/types/ui-state-message';
import type { SectionTitleTag } from '@federa/types/ui-text';

interface StateMessageBaseProps {
	/** Title text */
	title: string;
	/** Description text; absent, no paragraph is drawn. */
	description?: string;
	action?: StateMessageAction;
	/** Optional secondary action button (rendered as outline/ghost style) */
	secondaryAction?: StateMessageAction;
	iconElement: React.ReactNode;
	/** Additional CSS classes */
	className?: string;
	/** ARIA role for the container */
	role?: StateMessageRole;
	/** ARIA live setting */
	ariaLive?: StateMessageAriaLive;
	/** Size variant controlling padding and text sizing */
	size?: ComponentSize;
	/** The title's outline tier: `h3` by default, `h2` where the state sits straight under the page's `h1`. */
	titleTag?: SectionTitleTag;
}

const STATE_TITLE_BASE: React.CSSProperties = {
	color: colors.text.primary,
	fontWeight: typography.fontWeight.semibold,
};

/** Shared description styles for md/lg presets (sm uses MUTED_LABEL_SECONDARY). */
const STATE_DESC_BASE_MD_LG: React.CSSProperties = {
	color: colors.text.secondary,
	fontSize: typography.fontSize.base,
	marginTop: spacing[2],
};

/**
 * EACH GAP RIDES THE ELEMENT THAT FOLLOWS IT: the description carries the space above it, and the button row (`follow`)
 * carries its own. So the last element drawn never trails space below it -- a message with no button or no description
 * still sits on the centre it is drawn around.
 */
const SIZE_PRESETS: Record<
	ComponentSize,
	{
		container: React.CSSProperties;
		title: React.CSSProperties;
		description: React.CSSProperties;
		follow: React.CSSProperties;
	}
> = {
	sm: {
		container: {
			minHeight: 'auto',
			paddingTop: spacing[6],
			paddingBottom: spacing[6],
			paddingLeft: spacing[4],
			paddingRight: spacing[4],
		},
		title: {
			...STATE_TITLE_BASE,
			fontSize: typography.fontSize.base,
		},
		description: {
			...MUTED_LABEL_SECONDARY,
			lineHeight: typography.lineHeight.normal,
			marginTop: spacing[1],
		},
		follow: { marginTop: spacing[3] },
	},
	md: {
		container: {
			minHeight: spacing[100],
			paddingTop: spacing[16],
			paddingBottom: spacing[16],
			paddingLeft: spacing[6],
			paddingRight: spacing[6],
		},
		title: {
			...STATE_TITLE_BASE,
			fontSize: typography.fontSize['2xl'],
		},
		description: {
			...STATE_DESC_BASE_MD_LG,
			lineHeight: typography.lineHeight.normal,
		},
		follow: { marginTop: spacing[6] },
	},
	lg: {
		container: {
			minHeight: spacing[100],
			paddingTop: spacing[20],
			paddingBottom: spacing[20],
			paddingLeft: spacing[12],
			paddingRight: spacing[12],
		},
		title: {
			...STATE_TITLE_BASE,
			fontSize: typography.fontSize['3xl'],
		},
		description: {
			...STATE_DESC_BASE_MD_LG,
			lineHeight: typography.lineHeight.relaxed,
		},
		follow: { marginTop: spacing[6] },
	},
};

const stateButtonBaseStyle: React.CSSProperties = {
	...ACTION_BUTTON_PADDING_STYLE,
	borderRadius: borderRadius.md,
	fontSize: typography.fontSize.base,
	cursor: cursor.pointer,
};

const stateActionButtonStyle: React.CSSProperties = {
	...stateButtonBaseStyle,
	backgroundColor: colors.brand.primary,
	color: colors.brand.primaryForeground,
	border: 'none',
};

const stateSecondaryActionButtonStyle: React.CSSProperties = {
	...stateButtonBaseStyle,
	backgroundColor: 'transparent',
	color: colors.text.secondary,
	border: BORDER_DEFAULT,
};

/**
 * One action button; the primary/secondary split is the style token AND the variant, which now travel together.
 *
 * The variant is a prop rather than derived, because the hover treatment is the caller's decision and `Button` reads it
 * from the variant alone. That variant hover also REPLACES a `hover:opacity-90` class the button used to carry: `Button`
 * writes `opacity` inline, so a class-level hover opacity could never have taken effect underneath it.
 */
function StateActionButton({
	action,
	style,
	variant,
}: {
	action: StateMessageAction;
	style: React.CSSProperties;
	variant: Extract<ButtonVariant, 'primary' | 'outline'>;
}) {
	return (
		<Button variant={variant} size="lg" onClick={action.onClick} style={style}>
			{action.label}
		</Button>
	);
}

export function StateMessageBase({
	title,
	description,
	action,
	secondaryAction,
	iconElement,
	className = '',
	role = 'status',
	ariaLive,
	size = 'md',
	titleTag: Title = SECTION_TITLE_TAG_DEFAULT,
}: StateMessageBaseProps) {
	const hasActions = Boolean(action ?? secondaryAction);
	const preset = SIZE_PRESETS[size];

	return (
		<div className={`${LAYOUT_CLASSES.columnCenterAll} ${className}`} style={preset.container} role={role} aria-live={ariaLive}>
			{iconElement}

			<Title className="text-center" style={preset.title}>
				{title}
			</Title>

			{description === undefined ? null : (
				<p className="text-center max-w-md" style={preset.description}>
					{description}
				</p>
			)}

			{hasActions && (
				<div className="flex" style={{ ...preset.follow, gap: spacing[3] }}>
					{secondaryAction && <StateActionButton action={secondaryAction} style={stateSecondaryActionButtonStyle} variant="outline" />}
					{action && <StateActionButton action={action} style={stateActionButtonStyle} variant="primary" />}
				</div>
			)}
		</div>
	);
}
